import React, {useState} from "react";
import {
    Box, Typography, Accordion, AccordionSummary, AccordionDetails,
    Paper, Grid, Button
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import {NavLink, Outlet, useRouteLoaderData} from "react-router-dom";
import PageTitle from "../../../../components/PageTitle";

export default function ObjectRules() {
    const layoutLoader = useRouteLoaderData("obj_layout");
    const hotelObjs = layoutLoader?.hotelObj?.children_obj || [];
    const parentName = layoutLoader?.hotelObj?.name;
    const [expanded, setExpanded] = useState(null);

    const handleExpand = (panel) => (_, isExpanded) => {
        setExpanded(isExpanded ? panel : null);
    };

    return (
        <Box sx={{width: "100%", p: 3}}>
            <PageTitle title={`Правила об'єкта: ${parentName || layoutLoader?.id || "ID"}`} />
            {hotelObjs.length === 0 ? (
                <Typography variant="h6" color="error">Немає підоб'єктів</Typography>
            ) : hotelObjs.map((hotelObj) => {
                const {rates = [], rules = []} = hotelObj;

                // ID всех правил, привязанных к тарифам
                const usedRuleIds = new Set(rates.map(rate => rate.rules_id));
                const unusedRules = rules.filter(rule => !usedRuleIds.has(rule.id));

                return (
                    <Box key={hotelObj.id} sx={{mb: 6, pb: 2, borderBottom: "1px solid #ccc"}}>
                        <Typography variant="h5" sx={{fontWeight: "bold"}}>{hotelObj.name}</Typography>
                        <p>{hotelObj?.service_name}</p>

                        {/* ТАРИФЫ */}
                        {rates.map(rate => {
                            const rule = rules.find(r => r.id === rate.rules_id);

                            return (
                                <Accordion
                                    key={rate.id}
                                    expanded={expanded === `rate-${rate.id}`}
                                    onChange={handleExpand(`rate-${rate.id}`)}
                                    sx={{mt: 2}}
                                >
                                    <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                                        <Typography variant="h6">{rate.name} ({rate.active})</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {rule ? (
                                            <RuleDetails rule={rule}/>
                                        ) : (
                                            <Typography color="error">Правило не знайдено</Typography>
                                        )}
                                        <Box sx={{mt: 2, textAlign: "right"}}>
                                            <Button component={NavLink}
                                                    to={`${rule?.id}/edit?id=${rule?.id}`}
                                                    variant="contained"
                                                    startIcon={<EditIcon/>}>
                                                Редагувати
                                            </Button>
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            );
                        })}

                        {/* НЕПРИВЯЗАНЫЕ ПРАВИЛА */}
                        {unusedRules.length > 0 && (
                            <>
                                <Typography variant="subtitle1" sx={{mt: 3, mb: 1}}>
                                    Не прив’язані правила:
                                </Typography>
                                {unusedRules.map(rule => (
                                    <Accordion
                                        key={rule.id}
                                        expanded={expanded === `rule-${rule.id}`}
                                        onChange={handleExpand(`rule-${rule.id}`)}
                                    >
                                        <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                                            <Typography>{rule.name}</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <RuleDetails rule={rule}/>
                                            <Box sx={{mt: 2, textAlign: "right"}}>
                                                <Button component={NavLink}
                                                        to={`${rule.id}/edit?id=${rule.id}`}
                                                        variant="contained"
                                                        startIcon={<EditIcon/>}>
                                                    Редагувати
                                                </Button>
                                            </Box>
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                            </>
                        )}
                        <Button component={NavLink}
                                variant="contained"
                                to={`new?children_id=${hotelObj.id}`}
                                color="primary"
                                startIcon={<EditIcon/>}
                                sx={{ mt: 3 }}
                        >
                            Додати правило скасування
                        </Button>
                    </Box>
                );
            })}
            <Outlet/>
        </Box>
    );
}

// 🔍 Вспомогательный компонент для отображения правил
function RuleDetails({rule}) {
    return (
        <Grid container spacing={2}>
            <Grid item xs={6}><Typography variant="subtitle2">Час
                заїзду:</Typography><Typography>{rule.date_arrived}</Typography></Grid>
            <Grid item xs={6}><Typography variant="subtitle2">Час
                виїзду:</Typography><Typography>{rule.date_arrived_out}</Typography></Grid>
            <Grid item xs={12}><Typography variant="subtitle2">Ліжка для
                дітей:</Typography><Typography>{rule.child_beds || "—"}</Typography></Grid>
            <Grid item xs={6}><Typography
                variant="subtitle2">Передплата:</Typography><Typography>{rule.prepayment ? "Так" : "Ні"}</Typography></Grid>
            <Grid item xs={6}><Typography variant="subtitle2">Політика
                скасування:</Typography><Typography>{getCancellationPolicy(rule.cancellation_policy)}</Typography></Grid>
            <Grid item xs={6}><Typography variant="subtitle2">Сума
                скасування:</Typography><Typography>{getCancellationSumm(rule.cancellation_summ)}</Typography></Grid>
            <Grid item xs={6}><Typography variant="subtitle2">Сума за
                незаїзд:</Typography><Typography>{getCancellationSummNoArrived(rule.cancellation_summ_no_arrived)}</Typography></Grid>
            <Grid item xs={6}><Typography variant="subtitle2">Метод
                стягнення:</Typography><Typography>{getCancellationMethod(rule.cancellation_summ_get_method)}</Typography></Grid>
            <Grid item xs={6}><Typography variant="subtitle2">Час повернення
                коштів:</Typography><Typography>{getCancellationMethodSummGet(rule.cancellation_summ_method_get)}</Typography></Grid>
            <Grid item xs={12}><Typography variant="subtitle2">Оплата
                проживання:</Typography><Typography>{rule.payment_option_for_accommodation}</Typography></Grid>
        </Grid>
    );
}

// --- Расшифровка значений (можно вынести в utils при желании)

const getCancellationPolicy = (key) => {
    const choices = {
        "any_time": "Будь-коли",
        "day_of_arrival": "За день до заїзду",
        "two_days_before": "За 2 дні до заїзду",
        "three_days_before": "За 3 дні до заїзду",
        "five_days_before": "За 5 днів до заїзду",
        "week_before": "За 7 днів до заїзду",
        "no_refund": "Без повернення"
    };
    return choices[key] || "Невідомо";
};

const getCancellationSumm = (key) => {
    const choices = {
        "first_night": "Вартість першої ночі",
        "50_percent": "50% від вартості бронювання",
        "100_percent": "100% від вартості бронювання",
        "custom": "Ввести суму вручну"
    };
    return choices[key] || "Невідомо";
};

const getCancellationSummNoArrived = (key) => {
    const choices = {
        "cancel_summ_booking": "Такий же як за скасування бронювання",
        "first_night": "Вартість першої ночі",
        "50_percent": "50% від вартості бронювання",
        "100_percent": "100% від вартості бронювання",
        "custom": "Ввести суму вручну"
    };
    return choices[key] || "Невідомо";
};

const getCancellationMethod = (key) => {
    const choices = {
        "banking_check": "Банківським переказом",
        "credit_card": "Кредитною карткою",
        "PayPal": "PayPal",
        "custom": "Інше"
    };
    return choices[key] || "Невідомо";
};

const getCancellationMethodSummGet = (key) => {
    const choices = {
        "7_days": "Протягом 7 днів",
        "14_days": "Протягом 14 днів"
    };
    return choices[key] || "Невідомо";
};
